import recaptcha from '@components/App/RecaptchaWidget/recaptcha'
import { useEffect, useRef } from 'react'

import API from '@/api'
import { useAuth, useFeatureFlags } from '@/hooks'

type SitemapImportOptions = {
  urls: string[]
  options: {
    meta: boolean
  }
  onError: (errors: string) => void
  onComplete: (result: any, url: string) => void
}

export const useSitemapImport = () => {
  const { id: userId } = useAuth()
  const { crawler: featureFlags } = useFeatureFlags()
  const eventSource = useRef<EventSource | null>(null)
  // The stream is opened after an await, so a second import can start before the first one
  // has a stream to close. Every import takes a version; the newest version owns the ref.
  const version = useRef(0)

  // An EventSource that is not closed keeps the crawler connection open for the
  // lifetime of the tab, and redials by itself when the server ends the stream.
  // Closing a superseded stream must leave the ref alone — it belongs to the import
  // that superseded it.
  const closeStream = (stream: EventSource | null = eventSource.current) => {
    stream?.close()
    if (eventSource.current === stream) {
      eventSource.current = null
    }
  }

  // Unmounting has to invalidate every version, not just close what is open: an import still
  // waiting on its request would otherwise open its stream afterwards and store it on a ref
  // nothing reads again. Bumping the version makes that stream close itself instead.
  useEffect(
    () => () => {
      version.current += 1
      closeStream()
    },
    []
  )

  const createEventSource = async (id: string, importVersion: number) => {
    const token = await recaptcha.execute({ action: 'createEventSource' })
    const url = new URL(`${import.meta.env.VITE_GENERATOR_URL!}/sitemap/active`)
    url.searchParams.append('userId', userId)
    url.searchParams.append('response_key', token)
    url.searchParams.append('jobId', id)
    const events = new EventSource(url.toString())
    if (importVersion === version.current) {
      eventSource.current = events
      return events
    }
    // Superseded while this stream was opening: nothing tracks it, so close it here or it
    // stays connected for the lifetime of the tab.
    events.close()
    return events
  }

  const importUrls = async ({
    urls,
    options,
    onComplete,
    onError,
  }: SitemapImportOptions) => {
    closeStream()

    version.current += 1
    const importVersion = version.current
    const isCurrent = () => importVersion === version.current

    const reportNotFound = () => onError(`Sitemap.xml on ${urls[0]} not found`)

    API.Import.sitemap({
      urls,
      options,
      userId,
      pro: !!featureFlags.userHasMaximumLimit,
    })
      .then(({ id }) => createEventSource(id, importVersion))
      .then((events) => {
        // CONNECTING means EventSource is retrying a dropped connection by itself — the job keeps
        // running server side, so let it. CLOSED means it gave up (the stream answered non-2xx)
        // and nothing more will arrive. A stream that is no longer the current one has already
        // been finished or superseded, and is not this import's to report on.
        events.onerror = () => {
          if (events.readyState !== EventSource.CLOSED || !isCurrent()) {
            return
          }
          closeStream(events)
          reportNotFound()
        }

        events.onmessage = async (event) => {
          const parsedResult = JSON.parse(event.data)
          switch (parsedResult.type) {
            case 'done':
              // Terminal: the crawler ends the stream here, so nothing more arrives. Closing
              // ourselves keeps EventSource from redialling a job that is already finished.
              closeStream(events)

              // A superseded import must not report over the one that replaced it.
              if (!isCurrent()) {
                break
              }

              if (parsedResult.data?.data?.length > 0) {
                const data = JSON.parse(parsedResult.data.data)
                if (data.result) {
                  onComplete(data.result, urls[0])
                } else {
                  reportNotFound()
                }
              } else {
                reportNotFound()
              }
              break

            default: {
              const { progress } = parsedResult
              // biome-ignore lint/suspicious/noConsole: intentional
              console.log('progress', progress)
            }
          }
        }
      })
      .catch(() => {
        // A newer import owns the ref by now — closing it would kill a live stream and
        // report this call's failure over a running import.
        if (!isCurrent()) {
          return
        }
        closeStream()
        reportNotFound()
      })
  }

  return { importUrls }
}
